/**
 * @file index.tsx
 * @module app/modules/finance-reports
 * @description Defines routes and page metadata for finance-report views and their report variants.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import { Navigate, Routes, Route, Outlet } from "react-router-dom";
import { PageLink, PageTitle } from "../../../_admin/layout/core";
import SalesReport from "./sales-report";
import ReturnProductReport from "./return-product-report";
import DeliveryReport from "./delivery-report";
import SalesReportV2 from "./sales-report-v2";
import SalesReportV3 from "./sales-report-v3";
import SalesReportV4 from "./sales-report-v4";
import SalesReportV5 from "./sales-report-v5";
import SalesReportV6 from "./sales-report-v6";
import SalesReportV8 from "./sales-report-v8";
import SalesReportV7 from "./sales-report-v7";
import SalesReportV9 from "./sales-report-v9";
import SalesReportV10 from "./sales-report-10";
import RouteProductReport from "./route-product-report";
import DriverReconciliationReport from "./driver-reconciliation-report";
import OutOfStockReport from "./out-of-stock-report";
import FinanceSalesReport from "./finance-sales-report";
import FinanceDeliveryReport from "./finance-delivered-report";
import FinanceReturnReport from "./finance-return-report";
import FinanceOrderReport from "./finance-order-report";
import OutwardReport from "./outward-report";
const financeReportBreadCrumbs: Array<PageLink> = [
  {
    title: "Finance Reports",
    path: "/finance-reports",
    isSeparator: false,
    isActive: false,
  },
  {
    title: "",
    path: "",
    isSeparator: true,
    isActive: false,
  },
];
const ProfilePage = () => (
  <Routes>
    <Route
      element={
        <>
          <Outlet />
        </>
      }
    >
      <Route
        path="outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report
            </PageTitle>
            <SalesReport />
          </>
        }
      />

      <Route
        path="v2-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V2)
            </PageTitle>
            <SalesReportV2 />
          </>
        }
      />
      <Route
        path="v3-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V3)
            </PageTitle>
            <SalesReportV3 />
          </>
        }
      />
      <Route
        path="v4-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V4)
            </PageTitle>
            <SalesReportV4 />
          </>
        }
      />
      <Route
        path="v5-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V5)
            </PageTitle>
            <SalesReportV5 />
          </>
        }
      />
      <Route
        path="v6-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V6)
            </PageTitle>
            <SalesReportV6 />
          </>
        }
      />
      <Route
        path="v7-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report(V7)
            </PageTitle>
            <SalesReportV7 />
          </>
        }
      />
      <Route
        path="v8-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Only Outward-Return Not Considered
            </PageTitle>
            <SalesReportV8 />
          </>
        }
      />
      <Route
        path="v9-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Reports With Return Combined
            </PageTitle>
            <SalesReportV9 />
          </>
        }
      />
      <Route
        path="v10-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report (Only Return Considered)
            </PageTitle>
            <SalesReportV10 />
          </>
        }
      />
      <Route
        path="return-product-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Return Product Report
            </PageTitle>
            <ReturnProductReport />
          </>
        }
      />
      <Route
        path="route-products-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Delivery Plan Report
            </PageTitle>
            <RouteProductReport />
          </>
        }
      />
      <Route
        path="delivery-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Delivery Report
            </PageTitle>
            <DeliveryReport />
          </>
        }
      />
      <Route
        path="driver-reconciliation-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Driver Reconciliation Report
            </PageTitle>
            <DriverReconciliationReport />
          </>
        }
      />
      {/* the report moved to UProcure; old links still work */}
      <Route path="ats-report" element={<Navigate to="/UProcure/inventory-replenishment-report" replace />} />
      {/* previous report, kept for side-by-side checks until the new one is signed off */}
      <Route
        path="out-of-stock-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Out of Stock Report (previous)
            </PageTitle>
            <OutOfStockReport />
          </>
        }
      />
      <Route
        path="finance-sales-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              SalesReport
            </PageTitle>
            <FinanceSalesReport />
          </>
        }
      />
      <Route
        path="finance-outward-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              Outward Report
            </PageTitle>
            <OutwardReport />
          </>
        }
      />
      <Route
        path="finance-order-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              OrdersReport
            </PageTitle>
            <FinanceOrderReport />
          </>
        }
      />
      <Route
        path="finance-delivery-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              DeliveryReport
            </PageTitle>
            <FinanceDeliveryReport />
          </>
        }
      />
      <Route
        path="finance-return-report"
        element={
          <>
            <PageTitle breadcrumbs={financeReportBreadCrumbs}>
              DeliveryReport
            </PageTitle>
            <FinanceReturnReport />
          </>
        }
      />
      <Route index element={<Navigate to="/finance-reports/sales-report" />} />
    </Route>
  </Routes>
);
export default ProfilePage;
